<template>
  <div class="theme-toggle">
    <button
      ref="pemicuRef"
      type="button"
      class="theme-toggle__trigger"
      :aria-label="$t('theme.toggle')"
      :aria-expanded="terbuka"
      aria-haspopup="menu"
      @click.stop="alihkan"
    >
      <i :class="currentIcon" aria-hidden="true"></i>
    </button>

    <!--
      Menu dipindahkan ke <body>. Di dalam footer ia dulu terbuka ke bawah
      dan langsung keluar layar; di dalam header ia terpotong oleh
      backdrop-filter. Lihat catatan lengkap di useMenuMengambang.js.
    -->
    <Teleport to="body">
      <transition name="popover">
        <div v-if="terbuka" ref="menuRef" class="theme-toggle__menu" role="menu" :style="gaya" @click.stop>
        <p class="theme-toggle__label">{{ $t('theme.label') }}</p>

        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          class="theme-toggle__option"
          :class="{ 'is-active': mode === option.value }"
          role="menuitemradio"
          :aria-checked="mode === option.value"
          @click="choose(option.value)"
        >
            <i :class="option.icon" aria-hidden="true"></i>
            <span>{{ $t(option.label) }}</span>
            <i v-if="mode === option.value" class="bi bi-check2 ms-auto" aria-hidden="true"></i>
          </button>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { useTheme } from '@/composables/useTheme';
import { useMenuMengambang } from '@/composables/useMenuMengambang';

export default {
  name: 'ThemeToggle',

  setup() {
    const { mode, isDark, setTheme } = useTheme();
    const menu = useMenuMengambang({ lebarMinimal: 200 });
    return { mode, isDark, setTheme, ...menu };
  },

  data() {
    return {
      options: [
        { value: 'light', label: 'theme.light', icon: 'bi bi-sun' },
        { value: 'dark', label: 'theme.dark', icon: 'bi bi-moon-stars' },
        { value: 'system', label: 'theme.system', icon: 'bi bi-circle-half' },
      ],
    };
  },

  computed: {
    currentIcon() {
      if (this.mode === 'system') return 'bi bi-circle-half';
      return this.isDark ? 'bi bi-moon-stars-fill' : 'bi bi-sun-fill';
    },
  },

  methods: {
    // Pendengar klik-luar, Escape, gulir, dan ubah-ukuran ditangani
    // useMenuMengambang — termasuk pembersihannya saat komponen dilepas.
    choose(value) {
      this.setTheme(value);
      this.tutup();
    },
  },
};
</script>

<style scoped>
.theme-toggle { display: inline-flex; }

.theme-toggle__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 1.05rem;
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.theme-toggle__trigger:hover {
  background-color: var(--bg-hover);
  color: var(--text-primary);
}

/*
 * Posisi (top/left/max-height) disetel dari JavaScript sebagai gaya inline
 * oleh useMenuMengambang. Di sini SENGAJA tidak ada top/right/bottom:
 * menyisakan `right: 0` sementara JS memasang `left` akan meregangkan menu
 * selebar layar.
 */
.theme-toggle__menu {
  position: fixed;
  z-index: var(--z-dropdown);
  min-width: 200px;
  padding: var(--space-2);
  background-color: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.theme-toggle__label {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
}

.theme-toggle__option {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-primary);
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  transition: background-color var(--transition-fast);
}

.theme-toggle__option:hover { background-color: var(--bg-hover); }

.theme-toggle__option.is-active {
  color: var(--text-brand);
  font-weight: 600;
}

.popover-enter-active,
.popover-leave-active {
  transition: opacity var(--transition-fast), transform var(--transition-fast);
}

.popover-enter-from,
.popover-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
